import { ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'

import Close from '@mui/icons-material/Close'
import { Stack } from '@mui/material'
import { ActionBarItem } from '@workwave-tidal/core/components/ActionBar'
import { FilePreview, FilePreviewMimeTypeIcon } from '@workwave-tidal/lab'

import { selectUserConfiguration } from '@/features/domain/user'
import { conversionUtils } from '@/server-data'

import { form } from '../../../formState'
import * as OrderTexts from '../../../intl'

import type { ExecutionEventsFormValues } from '../../../typings'
import type { OnRemovePOD, Props } from '../typings'

import { preview } from '@/components/form/preview'
import { PodListItemField } from '../fields/PodListItemField'

type PodAudioProps = {
  onRemove: OnRemovePOD
  audio: uui.domain.client.rm.MediaPod
  index: number
  userConfig: uui.domain.UserConfiguration
} & {
  onTogglePod: Props['onTogglePod']
  activePodMarker: Props['activePodMarker']
}

function PodAudio(props: PodAudioProps) {
  const { audio, onRemove, index, userConfig } = props
  const { sec, text } = audio

  const time = conversionUtils.convertTime(userConfig)(sec)
  const title = text || `${OrderTexts.getVoiceMemoLabel()} ${index}`

  const actions = [
    <ActionBarItem
      icon={<Close />}
      id="close"
      key="close"
      label={OrderTexts.getCloseButtonLabel()}
      onClick={() => onRemove(audio.uuid)}
    />,
  ]

  return (
    <FilePreview
      actions={actions}
      actionsVisibility="always"
      icon={<FilePreviewMimeTypeIcon preset="audio/mpeg" />}
      subtitle={time}
      title={title}
      tooltip={title}
      variant="standard"
      testId={`pod_audio_${index}`}
    />
  )
}

export function PodAudios(props: Props) {
  const { activePodMarker, onTogglePod, values: initialValue } = props
  const userConfig = useSelector(selectUserConfiguration)

  const renderItems = useMemo(
    () =>
      (values: uui.domain.client.rm.Pod[], onRemove: OnRemovePOD): ReactNode => {
        return (
          <Stack spacing={2}>
            {values.length > 0 &&
              values.map((audio, index) => (
                <PodAudio
                  audio={audio as uui.domain.client.rm.MediaPod}
                  index={index}
                  activePodMarker={activePodMarker}
                  onTogglePod={onTogglePod}
                  userConfig={userConfig}
                  onRemove={onRemove}
                  key={index}
                />
              ))}
            {values.length === 0 && <preview.Value>{OrderTexts.getNoneText()}</preview.Value>}
          </Stack>
        )
      },
    [activePodMarker, onTogglePod, userConfig],
  )

  return (
    <>
      {initialValue.formPods.audios.length > 0 && (
        <Stack spacing={1}>
          <form.SectionSubtitle>{OrderTexts.getVoiceMemosLabel()}</form.SectionSubtitle>

          <PodListItemField<'formPods.audios', ExecutionEventsFormValues>
            name="formPods.audios"
            podListItemFieldInputProps={{
              testId: 'pod-audios',
              renderItems,
            }}
          />
        </Stack>
      )}
    </>
  )
}
